import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Receipt, Plus, Pencil, Trash2, Star, Check, Globe , Percent, Building2, AlertCircle, History, Copy, Sparkles} from 'lucide-react';
import api from '@/lib/api';
import { invalidateConfigCache } from '@/lib/configCache';
import { invalidateTaxConfig } from '@/hooks/useTaxConfig';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';

export default function TaxSettingsPage() {
  const [taxRates, setTaxRates] = useState([]);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [editTax, setEditTax] = useState(null);
  const [taxForm, setTaxForm] = useState({ name: '', rate: 15, type: 'vat', is_default: false, description: '' });
  const [currencies, setCurrencies] = useState({ available: [], default: 'SAR', enabled: ['SAR'] });
  const [enabledCurrencies, setEnabledCurrencies] = useState(['SAR']);
  const [defaultCurrency, setDefaultCurrency] = useState('SAR');

  // Central VAT Config state
  const [taxConfig, setTaxConfig] = useState(null);
  const [countries, setCountries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);

  // Clone-from-template state
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneCountry, setCloneCountry] = useState('');
  const [cloneScope, setCloneScope] = useState('all'); // 'all' or comma list
  const [cloneOverride, setCloneOverride] = useState(true);
  const [cloning, setCloning] = useState(false);

  // Audit log state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);

  const fetchTaxRates = async () => {
    try {
      const res = await api.get('/accounting/tax-rates');
      setTaxRates(res.data);
    } catch { toast.error('Failed to load tax rates'); }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await api.get('/accounting/currencies');
      setCurrencies(res.data);
      setEnabledCurrencies(res.data.enabled || ['SAR']);
      setDefaultCurrency(res.data.default || 'SAR');
    } catch {}
  };

  const fetchTaxConfig = async () => {
    try {
      const [cfgRes, countriesRes, branchesRes] = await Promise.all([
        api.get('/accounting/tax-config'),
        api.get('/accounting/tax-config/countries'),
        api.get('/branches'),
      ]);
      setTaxConfig(cfgRes.data);
      setCountries(countriesRes.data);
      setBranches(branchesRes.data || []);
    } catch { toast.error('Failed to load tax config'); }
  };

  useEffect(() => { fetchTaxRates(); fetchCurrencies(); fetchTaxConfig(); }, []);

  const handleCountryChange = (code) => {
    const preset = countries.find(c => c.code === code);
    if (!preset) return;
    setTaxConfig({
      ...taxConfig,
      country_code: code,
      country_name: preset.name,
      default_vat_rate: preset.vat_rate,
      authority: preset.authority,
      currency: preset.currency,
      tax_label: preset.tax_label,
      trn_label: preset.trn_label,
      return_format: preset.return_format,
      vat_enabled: preset.vat_rate > 0,
    });
  };

  const updateBranchOverride = (branchId, field, value) => {
    setTaxConfig({
      ...taxConfig,
      branch_overrides: {
        ...(taxConfig.branch_overrides || {}),
        [branchId]: {
          ...(taxConfig.branch_overrides?.[branchId] || {}),
          [field]: value,
        },
      },
    });
  };

  // When user picks a country for a branch, auto-fill the derived fields
  // (rate, currency, authority, labels) so the row becomes instantly usable.
  const updateBranchCountry = (branchId, code) => {
    const preset = countries.find(c => c.code === code);
    if (!preset) return;
    setTaxConfig({
      ...taxConfig,
      branch_overrides: {
        ...(taxConfig.branch_overrides || {}),
        [branchId]: {
          ...(taxConfig.branch_overrides?.[branchId] || {}),
          country_code: code,
          country_name: preset.name,
          authority: preset.authority,
          currency: preset.currency,
          tax_label: preset.tax_label,
          trn_label: preset.trn_label,
          return_format: preset.return_format,
          vat_rate: preset.vat_rate,
          vat_enabled: preset.vat_rate > 0,
        },
      },
    });
  };

  const clearBranchOverride = (branchId) => {
    const overrides = { ...(taxConfig.branch_overrides || {}) };
    delete overrides[branchId];
    setTaxConfig({ ...taxConfig, branch_overrides: overrides });
  };

  const saveTaxConfig = async () => {
    if (!taxConfig) return;
    setSavingConfig(true);
    try {
      await api.put('/accounting/tax-config', taxConfig);
      toast.success('VAT configuration saved — applied across all modules');
      invalidateTaxConfig();
      fetchTaxConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSavingConfig(false);
  };

  const fetchAuditLog = async () => {
    try {
      const res = await api.get('/accounting/tax-config/audit-log?limit=50');
      setAuditEntries(res.data.items || []);
    } catch { toast.error('Failed to load audit log'); }
  };

  const applyCloneTemplate = async () => {
    if (!cloneCountry) { toast.error('Pick a country'); return; }
    setCloning(true);
    try {
      const body = { country_code: cloneCountry, override_existing: cloneOverride };
      if (cloneScope !== 'all') {
        body.branch_ids = cloneScope.split(',').filter(Boolean);
      }
      const res = await api.post('/accounting/tax-config/clone-template', body);
      toast.success(`Applied ${res.data.country?.name} to ${res.data.applied?.length || 0} branch(es)`);
      setCloneOpen(false);
      setCloneCountry('');
      invalidateTaxConfig();
      fetchTaxConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clone template');
    }
    setCloning(false);
  };

  const handleSaveTax = async () => {
    if (!taxForm.name) { toast.error('Name is required'); return; }
    try {
      if (editTax) {
        await api.put(`/accounting/tax-rates/${editTax.id}`, taxForm);
        toast.success('Tax rate updated');
      } else {
        await api.post('/accounting/tax-rates', taxForm);
        toast.success('Tax rate created');
      }
      setShowTaxModal(false);
      setEditTax(null);
      invalidateConfigCache('tax_rates');
      fetchTaxRates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDeleteTax = async (id) => {
    if (!window.confirm('Delete this tax rate?')) return;
    try {
      await api.delete(`/accounting/tax-rates/${id}`);
      toast.success('Deleted');
      fetchTaxRates();
    } catch { toast.error('Failed to delete'); }
  };

  const openNewTax = () => {
    setEditTax(null);
    setTaxForm({ name: '', rate: 15, type: 'vat', is_default: false, description: '' });
    setShowTaxModal(true);
  };

  const openEditTax = (tax) => {
    setEditTax(tax);
    setTaxForm({ name: tax.name, rate: tax.rate, type: tax.type, is_default: tax.is_default, description: tax.description || '' });
    setShowTaxModal(true);
  };

  const toggleCurrency = (code) => {
    setEnabledCurrencies(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const saveCurrencySettings = async () => {
    try {
      await api.put('/accounting/currencies', {
        default_currency: defaultCurrency,
        enabled_currencies: enabledCurrencies,
      });
      toast.success('Currency settings saved');
    } catch { toast.error('Failed to save'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="tax-settings-page">
        <SettingsPageHeader
          icon={Percent}
          title="Tax & Currency Settings"
          subtitle="Manage VAT rates, country tax regimes and currency preferences."
          accent="amber"
          titleTestId="page-title"
        />

        {/* iter689 — SSoT banner: reminds the operator that company
            identity fields (VAT, CR, address) flow from one central
            place, so they don't expect duplicate inputs here. */}
        <SSoTCompanyInfoBanner scopeLabel="Tax & VAT identity" />

        {/* Central VAT Configuration */}
        {taxConfig && (
          <div className="bg-white rounded-xl border-2 border-info/30 overflow-hidden" data-testid="vat-config-card">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-info/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-info" />
                <div>
                  <h2 className="text-lg font-semibold text-foreground">VAT & Tax Configuration</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Single source of truth — applied across Invoices, Sales, Expenses, Quick Entry & HR</p>
                </div>
              </div>
              <Button onClick={saveTaxConfig} disabled={savingConfig} size="sm" className="bg-info hover:bg-blue-700 text-white" data-testid="save-tax-config-btn">
                {savingConfig ? 'Saving…' : 'Save Configuration'}
              </Button>
            </div>

            <div className="p-5 space-y-5">
              {/* Country selector */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Country</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Select value={taxConfig.country_code} onValueChange={handleCountryChange}>
                      <SelectTrigger data-testid="country-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {countries.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name} — {c.vat_rate}% {c.vat_rate > 0 ? 'VAT' : '(no VAT)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Input
                      value={taxConfig.tax_number || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, tax_number: e.target.value })}
                      placeholder="Tax Registration # (e.g., ZATCA)"
                      data-testid="tax-number-input"
                    />
                  </div>
                  <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-3 py-2 border border-border">
                    <span className="text-xs text-muted-foreground">{taxConfig.authority || '-'} • {taxConfig.currency || 'SAR'}</span>
                  </div>
                </div>
              </div>

              {/* Default VAT settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">VAT Enabled</p>
                    <p className="text-[11px] text-muted-foreground">Apply VAT on transactions</p>
                  </div>
                  <Switch
                    checked={!!taxConfig.vat_enabled}
                    onCheckedChange={(v) => setTaxConfig({ ...taxConfig, vat_enabled: v })}
                    data-testid="vat-enabled-switch"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Default Rate (%)</p>
                  <Input
                    type="number"
                    step="0.1"
                    value={taxConfig.default_vat_rate || 0}
                    onChange={(e) => setTaxConfig({ ...taxConfig, default_vat_rate: parseFloat(e.target.value) || 0 })}
                    disabled={!taxConfig.vat_enabled}
                    data-testid="default-vat-rate"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">Default: Inclusive</p>
                    <p className="text-[11px] text-muted-foreground">Prices already include VAT</p>
                  </div>
                  <Switch
                    checked={!!taxConfig.vat_inclusive_default}
                    onCheckedChange={(v) => setTaxConfig({ ...taxConfig, vat_inclusive_default: v })}
                    disabled={!taxConfig.vat_enabled}
                    data-testid="vat-inclusive-switch"
                  />
                </div>
              </div>

              {/* Branch overrides */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Multi-Country Branch Configuration</label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setCloneOpen(true); }}
                      data-testid="clone-template-btn"
                      className="h-7 text-xs gap-1"
                    >
                      <Sparkles size={12} /> Clone from country template
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { fetchAuditLog(); setAuditOpen(true); }}
                      data-testid="audit-log-btn"
                      className="h-7 text-xs gap-1"
                    >
                      <History size={12} /> Audit log
                    </Button>
                    <span className="text-[11px] text-muted-foreground">Each branch can have its own country, tax authority &amp; TRN</span>
                  </div>
                </div>
                {branches.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic py-3">No branches configured.</p>
                ) : (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-xs text-muted-foreground">Branch</th>
                            <th className="text-left px-3 py-2 font-semibold text-xs text-muted-foreground min-w-[180px]">Country / Authority</th>
                            <th className="text-left px-3 py-2 font-semibold text-xs text-muted-foreground">Tax / VAT Number</th>
                            <th className="text-center px-3 py-2 font-semibold text-xs text-muted-foreground">Tax Enabled</th>
                            <th className="text-center px-3 py-2 font-semibold text-xs text-muted-foreground">Rate (%)</th>
                            <th className="text-center px-3 py-2 font-semibold text-xs text-muted-foreground">Inclusive</th>
                            <th className="text-center px-3 py-2 font-semibold text-xs text-muted-foreground" title="Reverse Charge Mechanism (cross-border B2B)">Reverse Charge</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {branches.map(b => {
                            const ov = taxConfig.branch_overrides?.[b.id];
                            const hasOverride = !!ov && Object.keys(ov).length > 0;
                            const branchCountryCode = ov?.country_code || taxConfig.country_code;
                            const branchPreset = countries.find(c => c.code === branchCountryCode);
                            return (
                              <tr key={b.id} className={`border-b border-border ${hasOverride ? 'bg-warning/10' : ''}`} data-testid={`branch-override-${b.id}`}>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <Building2 size={13} className="text-muted-foreground" />
                                    <span className="font-medium text-foreground">{b.name}</span>
                                    {hasOverride && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-warning/20 text-warning uppercase font-bold">Override</span>}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex flex-col gap-1">
                                    <Select
                                      value={branchCountryCode}
                                      onValueChange={(code) => updateBranchCountry(b.id, code)}
                                    >
                                      <SelectTrigger className="h-8 w-full" data-testid={`branch-country-${b.id}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {countries.map(c => (
                                          <SelectItem key={c.code} value={c.code}>
                                            {c.name} ({c.vat_rate}%)
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span className="text-[10px] text-muted-foreground">
                                      {branchPreset?.authority || '—'} · {branchPreset?.currency || '—'}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <Input
                                    value={ov?.tax_number ?? ''}
                                    onChange={(e) => updateBranchOverride(b.id, 'tax_number', e.target.value)}
                                    placeholder={branchPreset?.trn_label || 'Tax #'}
                                    className="h-8 w-36 text-xs"
                                    data-testid={`branch-trn-${b.id}`}
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Switch
                                    checked={ov?.vat_enabled ?? taxConfig.vat_enabled}
                                    onCheckedChange={(v) => updateBranchOverride(b.id, 'vat_enabled', v)}
                                    data-testid={`branch-enabled-${b.id}`}
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={ov?.vat_rate ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                      updateBranchOverride(b.id, 'vat_rate', val);
                                    }}
                                    placeholder={String(branchPreset?.vat_rate ?? taxConfig.default_vat_rate)}
                                    className="h-8 w-20 mx-auto text-center"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Switch
                                    checked={ov?.vat_inclusive ?? taxConfig.vat_inclusive_default}
                                    onCheckedChange={(v) => updateBranchOverride(b.id, 'vat_inclusive', v)}
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Switch
                                    checked={ov?.reverse_charge_enabled ?? false}
                                    onCheckedChange={(v) => updateBranchOverride(b.id, 'reverse_charge_enabled', v)}
                                    data-testid={`branch-rc-${b.id}`}
                                  />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  {hasOverride && (
                                    <button
                                      onClick={() => clearBranchOverride(b.id)}
                                      title="Remove override"
                                      data-testid={`clear-override-${b.id}`}
                                      className="p-1 rounded text-muted-foreground hover:bg-error/10 hover:text-error"
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="mt-2 flex items-start gap-2 p-3 bg-info/10 border border-info/30 rounded-lg">
                  <AlertCircle size={14} className="text-info mt-0.5 shrink-0" />
                  <p className="text-[11px] text-info leading-relaxed">
                    This configuration applies across <strong>Invoices, Sales, Expenses, Quick Entry, POS and HR payroll</strong>. Each branch can run a different country's tax regime with its own rate, inclusive/exclusive preference, TRN, and reverse-charge rules. The VAT Return report will auto-format per each branch's country (ZATCA, FTA, NBR, HMRC, generic, etc.). Changes take effect immediately after saving.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VAT / Tax Rates */}
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">VAT / Tax Rates</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Middle East standard: 15% VAT (Saudi Arabia)</p>
            </div>
            <Button onClick={openNewTax} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-tax-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Rate
            </Button>
          </div>
          <div className="divide-y divide-stone-50">
            {taxRates.map(tax => (
              <div key={tax.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors" data-testid={`tax-row-${tax.id}`}>
                <div className="flex items-center gap-3">
                  {tax.is_default && <Star className="w-4 h-4 text-warning fill-amber-500" />}
                  <div>
                    <p className="font-medium text-foreground">{tax.name}</p>
                    <p className="text-xs text-muted-foreground">{tax.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-foreground">{tax.rate}%</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${tax.type === 'vat' ? 'bg-info/10 text-info border-info/30' : 'bg-muted/50 text-muted-foreground border-border'}`}>
                    {tax.type}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => openEditTax(tax)} className="p-1.5 rounded-md hover:bg-muted">
                      <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button onClick={() => handleDeleteTax(tax.id)} className="p-1.5 rounded-md hover:bg-error/10">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Currency Settings */}
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Currency Settings</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Configure which currencies are available in your system</p>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">Default Currency</label>
              <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
                <SelectTrigger className="w-64" data-testid="default-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {enabledCurrencies.map(c => {
                    const cur = currencies.available.find(a => a.code === c);
                    return <SelectItem key={c} value={c}>{cur ? `${cur.code} - ${cur.name}` : c}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-3 block">Enabled Currencies</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {currencies.available.map(cur => (
                  <button key={cur.code} onClick={() => toggleCurrency(cur.code)}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                      enabledCurrencies.includes(cur.code)
                        ? 'border-orange-300 bg-orange-50'
                        : 'border-border hover:border-border'
                    }`} data-testid={`currency-${cur.code}`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      enabledCurrencies.includes(cur.code) ? 'border-orange-500 bg-orange-500' : 'border-border'
                    }`}>
                      {enabledCurrencies.includes(cur.code) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground">{cur.code} - {cur.symbol}</p>
                      <p className="text-xs text-muted-foreground">{cur.name} ({cur.country})</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <Button onClick={saveCurrencySettings} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-currency-btn">
                Save Currency Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Tax Rate Modal */}
        <Dialog open={showTaxModal} onOpenChange={setShowTaxModal}>
          <DialogContent data-testid="tax-modal">
            <DialogHeader>
              <DialogTitle>{editTax ? 'Edit Tax Rate' : 'Add Tax Rate'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Name *</label>
                <Input value={taxForm.name} onChange={e => setTaxForm({...taxForm, name: e.target.value})}
                  placeholder="e.g. VAT 15%" data-testid="tax-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Rate (%)</label>
                  <Input type="number" step="0.5" value={taxForm.rate}
                    onChange={e => setTaxForm({...taxForm, rate: parseFloat(e.target.value) || 0})} data-testid="tax-rate-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Type</label>
                  <Select value={taxForm.type} onValueChange={v => setTaxForm({...taxForm, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vat">VAT</SelectItem>
                      <SelectItem value="sales_tax">Sales Tax</SelectItem>
                      <SelectItem value="service_tax">Service Tax</SelectItem>
                      <SelectItem value="exempt">Exempt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Description</label>
                <Input value={taxForm.description} onChange={e => setTaxForm({...taxForm, description: e.target.value})}
                  placeholder="Optional description" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={taxForm.is_default}
                  onChange={e => setTaxForm({...taxForm, is_default: e.target.checked})}
                  className="rounded border-border" />
                <span className="text-sm text-muted-foreground">Set as default tax rate</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTaxModal(false)}>Cancel</Button>
              <Button onClick={handleSaveTax} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-tax-btn">
                {editTax ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clone country template dialog */}
        <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-amber-500"/> Clone Country Tax Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-muted-foreground">
                Pick a country and we'll auto-populate the rate, currency, authority,
                and tax labels for the selected branches in one click.
              </p>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Country</label>
                <Select value={cloneCountry} onValueChange={setCloneCountry}>
                  <SelectTrigger data-testid="clone-country-select"><SelectValue placeholder="Select a country..." /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name} — {c.vat_rate}% {c.tax_label || 'VAT'} · {c.currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Apply to</label>
                <Select value={cloneScope} onValueChange={setCloneScope}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches ({branches.length})</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name} only</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cloneOverride}
                  onChange={e => setCloneOverride(e.target.checked)}
                  className="rounded border-border" data-testid="clone-override-checkbox" />
                <span className="text-xs text-muted-foreground">Overwrite existing per-branch overrides</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloneOpen(false)}>Cancel</Button>
              <Button onClick={applyCloneTemplate} disabled={!cloneCountry || cloning} data-testid="apply-clone-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white">
                {cloning ? 'Applying...' : 'Apply Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit log dialog */}
        <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
          <DialogContent className="max-w-3xl max-h-[80dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><History size={16} className="text-info"/> Tax Configuration Audit Log</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2" data-testid="audit-log-list">
              {auditEntries.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No tax configuration changes recorded yet.</p>
              ) : auditEntries.map((a) => (
                <div key={a.id} className="border border-border rounded-lg p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-info/10 text-info font-mono text-[10px] uppercase">{a.action || 'update'}</span>
                      <span className="font-semibold text-foreground">{a.user_email || a.user_name || 'system'}</span>
                    </div>
                    <span className="text-muted-foreground text-[11px]">{(a.timestamp || '').replace('T', ' ').slice(0, 19)}</span>
                  </div>
                  {a.changed_keys && a.changed_keys.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {a.changed_keys.slice(0, 8).map((k, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[10px] font-mono">
                          {k.startsWith('branch:') ? `branch ${(branches.find(b => b.id === k.slice(7))?.name) || k.slice(7, 15)}` : k}
                        </span>
                      ))}
                    </div>
                  )}
                  {Object.keys(a.diff || {}).length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                      {Object.entries(a.diff).slice(0, 4).map(([k, v]) => (
                        <div key={k}>{k}: <span className="text-error">{JSON.stringify(v.before)}</span> → <span className="text-success">{JSON.stringify(v.after)}</span></div>
                      ))}
                    </div>
                  )}
                  {a.applied_branches && a.applied_branches.length > 0 && (
                    <div className="text-[11px] text-muted-foreground">
                      Applied to: {a.applied_branches.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAuditOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
